/** @jsx jsx */
import { createContext, Fragment, PropsWithChildren } from 'react';

import { css, jsx } from '@emotion/react';

import { Box, Stack } from '@atlaskit/primitives';
import { token, useThemeObserver } from '@atlaskit/tokens';

import { getBoxShadow, getBoxShadowAsList } from '../../utils';
import themeColorModeStyles from '../theme-color-mode-styles';
import type { ActiveTheme, TransformedTokenGrouped } from '../types';

import TokenButton from './token-button';
import {
  BorderWidth,
  Color,
  Elevation,
  Font,
  Label,
  Opacity,
  Radius,
  Space,
} from './token-button-variants';
import { TokenValue } from './token-definition';

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const ThemeBoundaryContext = createContext<ActiveTheme>({});

const ThemeBoundary = (
  props: PropsWithChildren<{ applyTheme?: ActiveTheme }>,
) => {
  const currentTheme = useThemeObserver();
  const { applyTheme = {}, children } = props;

  const { colorMode: currentColorMode = 'light' } = currentTheme;
  const normalisedTheme: ActiveTheme = { ...currentTheme, ...applyTheme };
  const { colorMode: applyColorMode = currentColorMode } = normalisedTheme;

  // Only apply CSS when it's needed to reset
  const themeCss =
    applyColorMode !== currentColorMode
      ? themeColorModeStyles[applyColorMode]
      : undefined;

  return (
    <ThemeBoundaryContext.Provider value={normalisedTheme}>
      <div css={themeCss}>{children}</div>
    </ThemeBoundaryContext.Provider>
  );
};

const tokenBorderStyles = css({
  borderColor: token('color.border'),
  borderStyle: 'solid',
  borderWidth: token('border.width'),
  paddingBlockEnd: `calc(${token('space.075')} - ${token('border.width')})`,
});

const tokenColorModeStyles = css({
  minWidth: '100%',
  maxWidth: '100%',
  padding: token('space.075'),
  backgroundColor: token('elevation.surface'),
  borderRadius: token('border.radius'),
  '&:focus': {
    backgroundColor: token('elevation.surface'),
    borderColor: token('color.border.focused'),
  },
  '&:hover': {
    backgroundColor: token('elevation.surface.hovered'),
  },
  '&:active': {
    backgroundColor: token('elevation.surface.pressed'),
  },
});

interface CopyButtonValueProps {
  variantLabel?: string;
  isFixedWidth?: boolean;
  className?: string;
  tokenValue: TokenValue;
  tokenOriginal: TransformedTokenGrouped;
}

const TokenButtonValue = ({
  tokenValue,
  tokenOriginal,
  variantLabel,
  className,
}: CopyButtonValueProps) => {
  const {
    value,
    attributes,
    original,
    testId,
    forceTheme: applyTheme,
  } = tokenValue;
  const { colorMode } = useThemeObserver();
  const { colorMode: applyColorMode = colorMode } = applyTheme ?? {};
  const group = attributes.group;
  const labelValue = () => {
    switch (group) {
      case 'shadow':
        return <BoxShadowValue boxShadowValue={value} />;
      case 'shape':
      case 'spacing':
      case 'typography':
        return value;
      default:
        return original.value;
    }
  };

  const copyValue = () => {
    switch (group) {
      case 'shadow':
        return getBoxShadow(value);
      case 'shape':
      case 'spacing':
      case 'typography':
        return value;
      default:
        return original.value;
    }
  };

  const hasTheme = !!applyTheme;
  const hasBorder = hasTheme && colorMode === applyColorMode;

  return (
    <ThemeBoundary applyTheme={applyTheme}>
      <TokenButton
        copyValue={copyValue()}
        variantLabel={variantLabel}
        className={className}
        testId={testId}
        css={[hasTheme && tokenColorModeStyles, hasBorder && tokenBorderStyles]}
      >
        {({ isHovered }) => (
          <Fragment>
            {group === 'opacity' && <Opacity value={value} />}
            {group === 'shadow' && <Elevation value={value} />}
            {(group === 'paint' || group === 'raw') && <Color value={value} />}
            {group === 'spacing' && <Space value={value} />}
            {group === 'shape' &&
              (original.value.startsWith('Radius') ? (
                <Radius value={value} />
              ) : (
                <BorderWidth value={value} />
              ))}
            {group === 'typography' && <Font tokenOriginal={tokenOriginal} />}
            <Label isHoverDisabled={hasTheme} isHovered={isHovered}>
              {labelValue()}
            </Label>
          </Fragment>
        )}
      </TokenButton>
    </ThemeBoundary>
  );
};

const BoxShadowValue = (props: { boxShadowValue: any }) => {
  const { boxShadowValue } = props;
  const boxShadowsAsList = getBoxShadowAsList(boxShadowValue);

  return (
    <Stack space="space.100">
      {boxShadowsAsList.map((value, index) => (
        <Box key={index}>{value}</Box>
      ))}
    </Stack>
  );
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default TokenButtonValue;
